import { ContentPanel } from '@/components/content-panel';
import { Card, Flex, Grid, LoadingOverlay, Text, Title } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { motion } from 'motion/react';
import { AssetStatus, MaterialType } from '../../../../../../../backend/src/types.generated';

import { useFacility } from '@/contexts/facility-context/hook';
import { useCallback, useEffect, useState } from 'react';
import { useFetchBagsForBaleCreateLazyQuery } from '../graphql.generated';
import { useBaleCreateContext } from '../hook';
import styles from '../styles.module.css';

export const BaleCreateMaterialConfirmPage = () => {
	const { setCurrentBale, resetWorkflow, setPage } = useBaleCreateContext();
	const [fetchBags] = useFetchBagsForBaleCreateLazyQuery();
	const [loading, setLoading] = useState(false);
	const { availableProductTypes, currentFacility } = useFacility();
	// We should only show product types that have bags available to include in the bale.
	const [filteredProductTypes, setFilteredProductTypes] = useState<
		{
			name: string;
			value: string;
			emoji: string;
		}[]
	>([]);

	const handleMaterialConfirm = (material: MaterialType) => {
		setCurrentBale((prev) =>
			prev
				? {
						...prev,
						material,
					}
				: prev
		);
		setPage('PHOTO');
	};

	const refreshFilteredProductTypes = useCallback(async () => {
		try {
			if (!currentFacility) return;
			setLoading(true);
			const types = await Promise.all(
				availableProductTypes.map(async (t) => {
					const { data } = await fetchBags({
						variables: {
							filter: {
								scanEvents: {
									facility: {
										id: currentFacility.id,
									},
								},
								actualMaterial: t.value as MaterialType,
								status: AssetStatus.BagReceived,
							},
						},
						fetchPolicy: 'network-only',
					});
					return {
						...t,
						count: data?.trackableAssets_aggregate?.count ?? 0,
					};
				})
			);
			setFilteredProductTypes(
				availableProductTypes.filter((t) =>
					types.some((t2) => t2.value === t.value && t2.count > 0)
				)
			);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [availableProductTypes, currentFacility, fetchBags]);

	useEffect(() => {
		refreshFilteredProductTypes();
	}, [refreshFilteredProductTypes]);

	return (
		<Flex
			pos="absolute"
			h="100vh"
			w="100%"
			className={`${styles.pageBackground} ${styles.pageContentWithHeader}`}
		>
			<Flex
				className={styles.materialConfirmHeader}
				pos="fixed"
				top={0}
				left={0}
				px="md"
				py="lg"
				bg="white"
				w="100%"
				align="center"
				gap="sm"
			>
				<IconArrowLeft onClick={resetWorkflow} />
				<Title order={2}>Creation Bale Scan</Title>
			</Flex>
			<LoadingOverlay zIndex={400} visible={loading} />
			<ContentPanel gap="sm">
				<motion.div
					initial={{ opacity: 0, scale: 0.8 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
				>
					<Flex direction="column">
						<Title order={2} ta="center">
							What material is in this bale?
						</Title>
					</Flex>
				</motion.div>
				<Grid w="100%" gutter="sm">
					{filteredProductTypes.filter(Boolean).map((materialType, index) => (
						<Grid.Col key={materialType.value} span={6}>
							<motion.div
								initial={{ opacity: 0, y: 20, scale: 0.9 }}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								transition={{
									duration: 0.4,
									delay: 0.4 + index * 0.1,
									ease: 'easeOut',
								}}
							>
								<Card
									shadow="sm"
									padding="sm"
									radius="md"
									withBorder
									className={styles.materialTypeCard}
									onClick={() => handleMaterialConfirm(materialType.value as MaterialType)}
								>
									<Flex direction="column" align="center" gap="xs">
										<Text className={`${styles.materialEmojiText} ${styles.emojiMedium}`}>
											{materialType?.emoji}
										</Text>
										<Text size="xs" ta="center" fw={500}>
											{materialType.name}
										</Text>
									</Flex>
								</Card>
							</motion.div>
						</Grid.Col>
					))}
					{Boolean(!loading && filteredProductTypes.length === 0) && (
						<Grid.Col span={12}>
							<Text c="dimmed" pt="xl" ta="center">
								There are no scanned bags available to be included in this bale.
							</Text>
						</Grid.Col>
					)}
				</Grid>
			</ContentPanel>
		</Flex>
	);
};
